import { useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';
import { ModifyWantedInformationBody } from 'api/wanted/wanted.types';
import { modifyWantedInformationMulti } from 'api/wanted/wanted';

import { ModifyWantedInformation } from './ModifyWantedInformationMulti';
import { ModifyWantedInformationContainerProps } from './ModifyWantedInformationMulti.types';
import { useModifyWantedInformationMultiValidation } from './ModifyWantedInformationMulti.validation';

export const ModifyWantedInformationContainer = ({ checkedOwners }: ModifyWantedInformationContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const { mutate: onModifyWantedInformationMulti, isLoading } = useMutation(modifyWantedInformationMulti, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyEnum.WANTED]);

      toast.dark(formatMessage({ id: AppMessages['wanted.multiAction.modify.success'] }));
      onModalClose();
    },
  });

  const { schema } = useModifyWantedInformationMultiValidation();
  const formMethods = useForm<ModifyWantedInformationBody>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  useEffect(() => {
    formMethods.reset({ informacja: '' });
  }, [isModalOpen]);

  const onSubmit = (formBody: ModifyWantedInformationBody) => {
    onModifyWantedInformationMulti({ body: { ...formBody, idWanted: checkedOwners.map(({ id }) => id) } });
  };

  return (
    <FormProvider {...formMethods}>
      <ModifyWantedInformation
        checkedOwners={checkedOwners}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
